// src/components/Templates.js
import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    TextField,
    Typography,
    IconButton,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    Paper,
    Alert,
    CircularProgress
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { getEmailTemplates, createEmailTemplate, updateEmailTemplate, deleteEmailTemplate } from '../api/emailTemplateApi';

export default function Templates({ affiliate }) {
    const [templates, setTemplates] = useState([]);
    const [openDialog, setOpenDialog] = useState(false);
    const [editingTemplate, setEditingTemplate] = useState(null);
    const [templateName, setTemplateName] = useState('');
    const [templateSubject, setTemplateSubject] = useState('');
    const [templateBody, setTemplateBody] = useState('');
    const [status, setStatus] = useState('');
    const [loading, setLoading] = useState(false);

    // Quill editor modules and formats configuration
    const modules = {
        toolbar: [
            [{ 'header': [1, 2, 3, false] }],
            ['bold', 'italic', 'underline', 'strike'],
            [{ 'color': [] }, { 'background': [] }],
            [{ 'list': 'ordered' }, { 'list': 'bullet' }],
            [{ 'align': [] }],
            ['link'],
            ['clean']
        ],
    };

    const formats = [
        'header',
        'bold', 'italic', 'underline', 'strike',
        'color', 'background',
        'list', 'bullet',
        'align',
        'link'
    ];

    // Load templates from API on mount
    useEffect(() => {
        if (affiliate) {
            loadTemplates();
        }
    }, [affiliate]);

    // Clean ReactQuill HTML to prevent double line breaks
    const cleanQuillHTML = (html) => {
        if (!html) return '';
        
        let cleaned = html;
        
        // Remove whitespace between tags
        cleaned = cleaned.replace(/>\s*\n\s*</g, '><');
        
        // ReactQuill automatically adds spacing after headers and lists
        // Remove <p><br></p> immediately after/before these elements to prevent double spacing
        cleaned = cleaned
            // After any heading (h1-h6)
            .replace(/<\/(h[1-6])><p><br><\/p>/gi, '</$1>')
            // After ordered list
            .replace(/<\/ol><p><br><\/p>/gi, '</ol>')
            // After unordered list  
            .replace(/<\/ul><p><br><\/p>/gi, '</ul>')
            // Before any heading (h1-h6)
            .replace(/<p><br><\/p><(h[1-6])>/gi, '<$1>')
            // Before ordered list
            .replace(/<p><br><\/p><ol>/gi, '<ol>')
            // Before unordered list
            .replace(/<p><br><\/p><ul>/gi, '<ul>');
        
        // Remove consecutive empty paragraphs (more than one in a row)
        cleaned = cleaned.replace(/(<p><br><\/p>){2,}/gi, '<p><br></p>');
        
        // Remove leading/trailing empty paragraphs
        cleaned = cleaned.replace(/^(<p><br><\/p>)+/gi, '');
        cleaned = cleaned.replace(/(<p><br><\/p>)+$/gi, '');
        
        return cleaned.trim();
    };

    const loadTemplates = async () => {
        try {
            setLoading(true);
            const data = await getEmailTemplates(affiliate);
            setTemplates(data);
        } catch (error) {
            console.error('Error loading templates:', error);
            setStatus('Error loading templates');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenDialog = (template = null) => {
        if (template) {
            // Editing existing template
            setEditingTemplate(template);
            setTemplateName(template.name);
            setTemplateSubject(template.subject);
            setTemplateBody(template.body);
        } else {
            // Creating new template
            setEditingTemplate(null);
            setTemplateName('');
            setTemplateSubject('');
            setTemplateBody('');
        }
        setOpenDialog(true);
        setStatus('');
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setEditingTemplate(null);
        setTemplateName('');
        setTemplateSubject('');
        setTemplateBody('');
        setStatus('');
    };

    const handleSaveTemplate = async () => {
        if (!templateName.trim()) {
            setStatus('Please enter a template name');
            return;
        }

        if (!templateSubject.trim()) {
            setStatus('Please enter a subject');
            return;
        }

        if (!templateBody.trim() || templateBody === '<p><br></p>') {
            setStatus('Please enter template content');
            return;
        }

        try {
            setLoading(true);
            
            // Clean the body HTML before saving
            const cleanedBody = cleanQuillHTML(templateBody);
            
            const templateData = {
                affiliateId: affiliate,
                name: templateName,
                subject: templateSubject,
                body: cleanedBody
            };

            if (editingTemplate) {
                // Update existing template
                await updateEmailTemplate(editingTemplate.id, templateData);
                setStatus('Template updated successfully!');
            } else {
                // Create new template
                await createEmailTemplate(templateData);
                setStatus('Template created successfully!');
            }

            // Reload templates from database
            await loadTemplates();
            handleCloseDialog();

            // Clear status after 3 seconds
            setTimeout(() => setStatus(''), 3000);
        } catch (error) {
            console.error('Error saving template:', error);
            setStatus('Error saving template');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteTemplate = async (templateId) => {
        if (window.confirm('Are you sure you want to delete this template?')) {
            try {
                setLoading(true);
                await deleteEmailTemplate(templateId);
                setStatus('Template deleted successfully!');
                await loadTemplates();
                setTimeout(() => setStatus(''), 3000);
            } catch (error) {
                console.error('Error deleting template:', error);
                setStatus('Error deleting template');
            } finally {
                setLoading(false);
            }
        }
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5" gutterBottom>
                    Message Templates
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenDialog()}
                >
                    Add Template
                </Button>
            </Box>

            {status && (
                <Alert severity={status.includes('Error') ? 'error' : 'success'} sx={{ mb: 2 }}>
                    {status}
                </Alert>
            )}

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 5 }}>
                    <CircularProgress />
                </Box>
            ) : templates.length === 0 ? (
                <Paper sx={{ p: 3, textAlign: 'center' }}>
                    <Typography variant="body1" color="textSecondary">
                        No templates yet. Create your first template to get started!
                    </Typography>
                </Paper>
            ) : (
                <List>
                    {templates.map((template) => (
                        <Card key={template.id} sx={{ mb: 2 }}>
                            <ListItem>
                                <ListItemText
                                    primary={
                                        <Typography variant="h6">
                                            {template.name}
                                        </Typography>
                                    }
                                    secondary={
                                        <Box>
                                            <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                                                <strong>Subject:</strong> {template.subject}
                                            </Typography>
                                            <Typography 
                                                variant="body2" 
                                                color="textSecondary" 
                                                sx={{ mt: 1 }}
                                                dangerouslySetInnerHTML={{ 
                                                    __html: template.body.substring(0, 100) + (template.body.length > 100 ? '...' : '') 
                                                }}
                                            />
                                        </Box>
                                    }
                                />
                                <ListItemSecondaryAction>
                                    <IconButton
                                        edge="end"
                                        aria-label="edit"
                                        onClick={() => handleOpenDialog(template)}
                                        sx={{ mr: 1 }}
                                    >
                                        <EditIcon />
                                    </IconButton>
                                    <IconButton
                                        edge="end"
                                        aria-label="delete"
                                        onClick={() => handleDeleteTemplate(template.id)}
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </ListItemSecondaryAction>
                            </ListItem>
                        </Card>
                    ))}
                </List>
            )}

            {/* Add/Edit Template Dialog */}
            <Dialog
                open={openDialog}
                onClose={handleCloseDialog}
                maxWidth="md"
                fullWidth
                fullScreen={window.innerWidth < 600}
            >
                <DialogTitle>
                    {editingTemplate ? 'Edit Template' : 'Create New Template'}
                </DialogTitle>
                <DialogContent>
                    <FormControl fullWidth margin="normal">
                        <TextField
                            label="Template Name"
                            value={templateName}
                            onChange={(e) => setTemplateName(e.target.value)}
                            placeholder="e.g., Welcome Message, Follow-up Email"
                            autoFocus
                        />
                    </FormControl>

                    <FormControl fullWidth margin="normal">
                        <TextField
                            label="Subject"
                            value={templateSubject}
                            onChange={(e) => setTemplateSubject(e.target.value)}
                            placeholder="Email subject line"
                        />
                    </FormControl>

                    <FormControl fullWidth margin="normal">
                        <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                            Template Body
                        </Typography>
                        <div style={{ border: '1px solid #ddd', borderRadius: '4px' }}>
                            <ReactQuill
                                theme="snow"
                                value={templateBody}
                                onChange={setTemplateBody}
                                modules={modules}
                                formats={formats}
                                style={{ height: '250px', marginBottom: '40px' }}
                            />
                        </div>
                    </FormControl>

                    {status && (
                        <Alert severity="warning" sx={{ mt: 2 }}>
                            {status}
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog} variant="outlined" disabled={loading}>
                        Cancel
                    </Button>
                    <Button onClick={handleSaveTemplate} variant="contained" disabled={loading}>
                        {loading ? 'Saving...' : (editingTemplate ? 'Update' : 'Create')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

